import { EditOutlined } from '@ant-design/icons';
import { DateField, TextField } from '@refinedev/antd';
import { Button, DatePicker, Flex, Form, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ReceptionShowLogisticProps {
  record?: Reception;
  refetch?: () => void;
}

const ReceptionShowLogistic = (props: ReceptionShowLogisticProps) => {
  const { t } = useTranslation();
  const { record, refetch } = props;
  const { userWorkArea } = useUserContext();
  const [openModal, setOpenModal] = useState<boolean>(false);

  return (
    <div className="ReceptionShowDataCard">
      <div className="ReceptionShowData">
        <b>{t('Observaciones')}</b>
        <TextField value={record?.comment ?? '-'} />
      </div>
      <div className="ReceptionShowData">
        <b>{t('Fecha de recepción')}</b>
        {record?.date ? <DateField value={record?.date} /> : '-'}
      </div>
      <div className="ReceptionShowData">
        <Flex gap={6}>
          <b>{t('Fecha de ingreso a operaciones')}</b>
          {userWorkArea == USER_WORK_AREA.LOGISTICS &&
            record?.status == PACKAGE_STATES.RECIBIDO && (
              <Button icon={<EditOutlined />} size="small" onClick={() => setOpenModal(true)} />
            )}
        </Flex>
        {/* Mostramos la fecha en formato local para la UI, aunque el backend la entregue en ISO. */}
        <TextField value={record?.entry_date ? dayjs(record.entry_date).format('L') : '-'} />
      </div>

      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}
        title={t('Editar fecha de ingreso a operaciones')}>
        <EntryDateNumberModal
          record={record!}
          closeModal={() => setOpenModal(false)}
          refetch={refetch}
        />
      </Modal>
    </div>
  );
};

interface UpdateModalProps {
  record: Reception;
  closeModal: () => void;
  refetch?: () => void;
}
const EntryDateNumberModal = (props: UpdateModalProps) => {
  const { t } = useTranslation();
  const { receptionRepository } = useRepositoriesContext();
  const { record, closeModal, refetch } = props;
  const [newEntryDate, setNewEntryDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    // El DatePicker de AntD trabaja con instancias de Dayjs, no con strings.
    setNewEntryDate(record?.entry_date ? dayjs(record.entry_date) : null);
  }, [record?.entry_date]);

  const onConfirmTrackingChange = useCallback(() => {
    if (!record?.id) return;
    // El backend espera una fecha ISO; la conversion visual a formato local queda solo para la UI.
    const formattedEntryDate = newEntryDate?.toISOString() || '';
    const currentEntryDate = record?.entry_date ? dayjs(record.entry_date) : null;

    if (
      (newEntryDate === null && !record?.entry_date) ||
      (newEntryDate !== null &&
        currentEntryDate?.isValid() &&
        newEntryDate.isSame(currentEntryDate))
    ) {
      closeModal();

      return;
    }

    void receptionRepository
      .updateReception(record.id, { entry_date: formattedEntryDate })
      .then(() => {
        closeModal();
        refetch?.();
      });
  }, [record, newEntryDate, receptionRepository, closeModal, refetch]);

  return (
    <div className="UpdateEntryDateModal">
      <Form onFinish={onConfirmTrackingChange} layout="vertical">
        <Form.Item style={{ marginBottom: 0 }}>
          <DatePicker
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
            style={{ width: '100%' }}
            value={newEntryDate}
            // Guardamos el valor nativo del DatePicker para evitar incompatibilidades de tipos.
            onChange={date => setNewEntryDate(date)}
          />
        </Form.Item>
        <Flex justify="end" gap={8} style={{ marginTop: 16 }}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Button type="primary" htmlType="submit">
            {t('Guardar')}
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

export default ReceptionShowLogistic;
